import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import axiosInstance from "../../utils/axiosInstance";

export const fetchPerformance = createAsyncThunk(
  "performance/fetchPerformance",
  async (data) => {
    const response = await axiosInstance(
      `/cq-performance?cohort_id=${data.cohort_id}`,
      "get",
    );
    return response.data.data;
  },
);

export const fetchLevels = createAsyncThunk(
  "performance/fetchLevels",
  async (id) => {
    const response = await axiosInstance(
      `/programs/${id}?include=capsules`,
      "get",
    );
    return response.data.data;
  },
);

export const fetchMilestoneFiles = createAsyncThunk(
  "performance/fetchMilestoneFiles",
  async (id) => {
    const response = await axiosInstance(
      `/milestone-files?squad_id=${id}`,
      "get",
    );
    return response.data.data;
  },
);

/**
 * Take-latest ordering guard for `fetchPerformance`, copied from the
 * `latestMissionRequestId` pattern in `store/user/slice.js`.
 *
 * `fetchPerformance` is dispatched from an effect keyed on the cohort, so a
 * cohort switch can put two requests in flight at once and the OLDER one can
 * land last. Scores are the payload here, so an out-of-order landing does not
 * merely look stale: it attributes one cohort's measured evidence to another.
 *
 * A stale action is dropped WHOLE, `isLoading` included. Letting a stale
 * rejection clear the loading flag would report the newer, still-running
 * request as finished.
 *
 * A null `latestRequestId` means we hold no ordering opinion yet (a rehydrated
 * session, a dispatch that never passed through `pending`), so we accept.
 */
const isStaleRequest = (state, action) =>
  state.latestRequestId != null &&
  action.meta?.requestId !== state.latestRequestId;

const performanceSlice = createSlice({
  name: "performance",
  initialState: {
    performance: null,
    levels: null,
    milestoneFiles: null,
    isLoading: false,
    // Chunk 3a. `error` records a failed fetch so a rejection can never be read
    // as an absence of scores.
    //
    // The two cohort fields are NOT interchangeable and the distinction is the
    // whole guard:
    //   `requestedCohortId` is the cohort a request is IN FLIGHT for. It moves
    //     on `pending`, before any rows for that cohort exist.
    //   `loadedCohortId` is the cohort the HELD rows were fetched for. It moves
    //     only when a matching `fulfilled` lands, and is cleared whenever the
    //     rows are cleared.
    // Reads go through `loadedCohortId`. Reading through `requestedCohortId`
    // would hand the previous cohort's rows out under the new cohort's identity
    // for the whole in-flight window, which is precisely the mislabeling this
    // guard exists to prevent.
    error: null,
    requestedCohortId: null,
    loadedCohortId: null,
    // The cohort the HELD error belongs to. It moves only with `error`, and
    // for the same reason `loadedCohortId` exists: between the active cohort
    // flipping and the effect dispatching `pending`, the slice still holds the
    // PREVIOUS cohort's outcome, and an error read without this comparison is
    // reported under the new cohort's identity.
    errorCohortId: null,
    latestRequestId: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchPerformance.pending, (state, action) => {
      state.isLoading = true;
      state.latestRequestId = action.meta?.requestId ?? null;
      const nextCohortId = action.meta?.arg?.cohort_id ?? null;
      // A request for a DIFFERENT cohort makes the held rows unreadable from
      // this moment on, not from the moment the new rows arrive. Dropping them
      // here is the belt; the selector's `loadedCohortId` comparison is the
      // braces for the render between the cohort flipping and this dispatch.
      // A refetch of the SAME cohort deliberately keeps the rows on screen.
      if (
        state.loadedCohortId !== null &&
        Number(state.loadedCohortId) !== Number(nextCohortId)
      ) {
        state.performance = null;
        state.loadedCohortId = null;
      }
      state.requestedCohortId = nextCohortId;
      // A new request supersedes the previous outcome. Without this, an error
      // from the previous cohort would still be sitting in state after this
      // request succeeds. Clearing unconditionally covers the different-cohort
      // case and the same-cohort retry alike.
      state.error = null;
      state.errorCohortId = null;
    });
    builder.addCase(fetchPerformance.fulfilled, (state, action) => {
      if (isStaleRequest(state, action)) return;
      // Normalize payload to always be an array in { data: [...] } structure.
      // The API may return an array directly, or a wrapper like { data: [...] }.
      const payload = action.payload;
      let dataArray;
      if (Array.isArray(payload)) {
        dataArray = payload;
      } else if (payload && Array.isArray(payload.data)) {
        dataArray = payload.data;
      } else {
        dataArray = [];
      }
      state.performance = { data: dataArray };
      // Bind the held rows to the cohort THIS request asked for, taken from the
      // matched action rather than from `requestedCohortId`, which by now may
      // already name a later request.
      state.loadedCohortId = action.meta?.arg?.cohort_id ?? null;
      state.isLoading = false;
      state.error = null;
      state.errorCohortId = null;
    });
    builder.addCase(fetchPerformance.rejected, (state, action) => {
      if (isStaleRequest(state, action)) return;
      state.isLoading = false;
      // Clearing the payload is the point. A failed fetch that leaves the
      // previous cohort's rows in place renders them under the new cohort's
      // heading, which is the one outcome evidence labeling exists to prevent.
      state.performance = null;
      state.loadedCohortId = null;
      state.error =
        action.error?.message ?? "performance could not be loaded right now";
      // Bind the error to the cohort THIS request asked for, taken from the
      // matched action rather than from `requestedCohortId`, which by now may
      // already name a later request.
      state.errorCohortId = action.meta?.arg?.cohort_id ?? null;
    });
    builder.addCase(fetchLevels.fulfilled, (state, action) => {
      state.levels = action.payload;
    });
    builder.addCase(fetchMilestoneFiles.fulfilled, (state, action) => {
      state.milestoneFiles = action.payload;
    });
  },
});

/**
 * The only sanctioned read of the performance payload.
 *
 * Returns the held rows ONLY when they were FETCHED for `cohortId`, which is
 * what `loadedCohortId` records. During a cohort switch this reports null for
 * the whole window rather than letting the page label one cohort's evidence
 * with another cohort's identity: null from the render on which the active
 * cohort flips (this comparison), and null again from `pending`, which drops
 * the rows outright.
 *
 * Ids are compared numerically because the cohort id reaches the two sides by
 * different routes: the thunk argument comes from the user payload, the
 * comparison argument from the same payload read at a different depth, and
 * either can be a numeric string.
 */
export const selectPerformanceForCohort = (state, cohortId) => {
  const slice = state?.performance;
  if (!slice) return null;
  if (cohortId === null || cohortId === undefined) return null;
  const loaded = slice.loadedCohortId;
  if (loaded === null || loaded === undefined) return null;
  if (Number(loaded) !== Number(cohortId)) return null;
  return slice.performance;
};

/**
 * The only sanctioned read of the load error.
 *
 * Scoped exactly like `selectPerformanceForCohort`, and for the same window:
 * `selectPerformanceForCohort` already returns no rows the moment the active
 * cohort flips, so without this comparison `GrowthTrend` would render once with
 * the PREVIOUS cohort's error over the new cohort's empty state, before the
 * effect dispatches `pending` and clears it.
 *
 * Ids are compared numerically for the same reason as in the rows selector:
 * either side can arrive as a numeric string.
 */
export const selectLoadErrorForCohort = (state, cohortId) => {
  const slice = state?.performance;
  if (!slice) return null;
  if (slice.error === null || slice.error === undefined) return null;
  if (cohortId === null || cohortId === undefined) return null;
  const errored = slice.errorCohortId;
  if (errored === null || errored === undefined) return null;
  if (Number(errored) !== Number(cohortId)) return null;
  return slice.error;
};

export default performanceSlice.reducer;
